import { useEffect, useRef, useState } from 'react';

const prefersStill = () => window.matchMedia('(prefers-reduced-motion: reduce)').matches || Boolean(navigator.connection?.saveData);

// The two services videos loop continuously. Scroll position never pauses them.
// User/device motion preferences and browser playback restrictions still apply.
export const useServicesVideo = ({ assetName = 'betaris-services', posterName = 'services-poster' } = {}) => {
  const videoRef = useRef(null);
  const areaRef = useRef(null);
  const [size, setSize] = useState(() => window.matchMedia('(max-width: 760px)').matches ? 'mobile' : 'desktop');
  const [extension] = useState(() => document.createElement('video').canPlayType('video/mp4; codecs="avc1.640020"') ? 'mp4' : 'webm');
  const asset = `/media/${assetName}-${size}.${extension}`;
  const poster = `/media/${posterName}-${size}.webp`;
  const [source, setSource] = useState(null);
  const [motionRestricted, setMotionRestricted] = useState(prefersStill);
  const [explicitStart, setExplicitStart] = useState(false);
  const [wanted, setWanted] = useState(() => !prefersStill());
  const [resumeCounter, setResumeCounter] = useState(0);
  const [playing, setPlaying] = useState(false);
  const [hasPlayed, setHasPlayed] = useState(false);
  const [error, setError] = useState(false);
  const [blocked, setBlocked] = useState(false);
  const canRun = wanted && (!motionRestricted || explicitStart);
  const allowed = useRef(canRun);
  allowed.current = canRun;

  useEffect(() => {
    const video = videoRef.current;
    const breakpoint = window.matchMedia('(max-width: 760px)');
    const motion = window.matchMedia('(prefers-reduced-motion: reduce)');
    const resize = event => {
      video?.pause(); setSize(event.matches ? 'mobile' : 'desktop');
      setSource(null); setPlaying(false); setHasPlayed(false); setError(false); setBlocked(false);
    };
    const resume = () => { if (!document.hidden) setResumeCounter(previous => previous + 1); };
    const preference = () => {
      const restricted = prefersStill();
      setMotionRestricted(restricted);
      if (restricted) {
        allowed.current = false;
        video?.pause(); setExplicitStart(false); setWanted(false);
        setSource(null); setHasPlayed(false); setPlaying(false);
      } else setWanted(true);
    };
    breakpoint.addEventListener('change', resize);
    motion.addEventListener('change', preference);
    preference();
    document.addEventListener('visibilitychange', resume);
    window.addEventListener('focus', resume);
    return () => {
      video?.pause(); breakpoint.removeEventListener('change', resize);
      motion.removeEventListener('change', preference);
      document.removeEventListener('visibilitychange', resume); window.removeEventListener('focus', resume);
    };
  }, []);

  useEffect(() => {
    // Also check at attachment time: preference emulation/device settings may
    // settle after React's initial render, or change while the route stays mounted.
    if (prefersStill() && !explicitStart) {
      setMotionRestricted(true); setWanted(false); setSource(null); setHasPlayed(false);
      return;
    }
    if (canRun && !source) setSource(asset);
  }, [asset, canRun, source, explicitStart]);
  useEffect(() => {
    const video = videoRef.current;
    if (!video) return;
    let cancelled = false;
    if (!canRun || !source) { video.pause(); setPlaying(false); }
    else {
      video.muted = true;
      video.play()?.catch(reason => {
        if (!cancelled && reason.name !== 'AbortError') {
          setWanted(false); setPlaying(false); setBlocked(reason.name === 'NotAllowedError');
          setError(reason.name !== 'NotAllowedError');
        }
      });
    }
    return () => { cancelled = true; };
  }, [canRun, source, resumeCounter]);

  // A start-only fallback appears only for reduced motion, autoplay denial or a
  // media failure. There is deliberately no pause toggle in the normal UI.
  const start = () => {
    const video = videoRef.current;
    if (!video) return;
    allowed.current = true;
    setExplicitStart(true); setWanted(true); setError(false); setBlocked(false);
    if (!source) { video.src = asset; setSource(asset); }
    if (error) video.load();
    video.muted = true;
    video.play()?.catch(reason => {
      if (reason.name !== 'AbortError') {
        setWanted(false); setPlaying(false);
        setBlocked(reason.name === 'NotAllowedError'); setError(reason.name !== 'NotAllowedError');
      }
    });
  };
  const onPlaying = () => {
    if (!allowed.current) { videoRef.current?.pause(); return; }
    setPlaying(true); setHasPlayed(true); setError(false); setBlocked(false);
  };
  const onError = () => { setError(true); setWanted(false); setPlaying(false); setHasPlayed(false); };
  const renderedSource = motionRestricted && !explicitStart ? null : source;
  return { areaRef, videoRef, source: renderedSource, poster, canRun, playing, hasPlayed, error, blocked, start,
    onPlaying, onPause: () => setPlaying(false), onError };
};